// askVestaHandoff.js — m3-15 "Ask Vesta about this" board↔chat loop (pure, node-testable).
//
// The canonical cross-surface contract (DESIGN-INTEGRATION §2.3, docs/01 §"Ask Vesta loop"):
// the board's Session Drawer footer calls onAsk(text, taskRef); the host (the two-mode shell)
// must (1) ENTER agent mode, (2) attach the task as the chat composer reference (the pill),
// (3) light the matching board card. The pill glow and the lit-card glow are intentionally the
// SAME — they read as one link.
//
// This module computes the resulting shell state transition from (text, taskRef). The React
// shell applies it (setState + focus ping); keeping the transition pure lets the gate prove the
// contract without React: same task → pill + lit card derive from ONE askRef (no drift).

// Compute the next shell state for an Ask-Vesta handoff. Returns null when the handoff is a
// no-op (no task ref / empty text) so the shell leaves state untouched.
//
// @param current  — { mode, askRef } (the shell's current state)
// @param text     — the question typed into the drawer footer
// @param taskRef  — { id, name, color? } the card's stable task reference
export function askFromProgress(current, text, taskRef) {
  if (!taskRef || !taskRef.id) return null
  const q = (text || "").trim()
  return {
    mode: "agent",        // (1) enter agent mode — the loop always lands in the Studio
    askRef: taskRef,      // (2) attach the pill (composer reference)
    composerSeed: q,      // prefill the composer with the question
    pingFocus: true,      // ping the composer to focus
  }
}

// The lit-card id derived from the active askRef — this is the SINGLE source for BOTH the
// composer pill (askRef) and the board card glow (litTask), so they can never disagree.
export function litTaskFor(askRef) {
  return askRef && askRef.id ? askRef.id : null
}

// Clearing the reference (× on the pill, or sending the message) drops BOTH glows at once.
export function clearAsk() {
  return { askRef: null, composerSeed: "", pingFocus: false }
}

// Gate helper: the pill ref and the lit card must reference the SAME task id — proves the two
// ends of the loop are driven by one source.
export function pillAndCardAgree(askRef) {
  const lit = litTaskFor(askRef)
  const pillId = askRef && askRef.id ? askRef.id : null
  return lit === pillId
}

// CHAT-7: decide how the chat must dispatch an auto-send (board→chat handoff OR a normal send).
// The handoff effect previously gated the send on `if (activeChat && companySlug)`, so when the
// user clicked "Ask Vesta about this task" with NO active chat, the question + spinner showed but
// NOTHING was sent (createChat was never called) — a forever-spinner dead-end. doSend handles this
// correctly: when activeChat is null it CREATES a chat. This pure helper mirrors doSend's branch so
// both paths agree. Returns one of:
//   { action: "noop" }                          — no companySlug (can't send at all)
//   { action: "post",   companySlug, chatId }   — existing chat → POST a follow-up message
//   { action: "create", companySlug }           — no active chat → create the chat with this turn
export function decideSend({ activeChat, companySlug }) {
  if (!companySlug) return { action: "noop" }
  if (activeChat) return { action: "post", companySlug, chatId: activeChat }
  return { action: "create", companySlug }
}
